<template>
  <div class="garden-root">
    <!-- Imagem de fundo -->
    <div
      class="garden-bg"
      :style="{ backgroundImage: 'url(/brain-garden.jpg)', opacity: imgOpacity }"
    />
    <!-- Overlay — adapta opacidade ao tema -->
    <div class="garden-overlay" :style="{ opacity: effectiveOverlay }" />
    <!-- Canvas com partículas e blooms -->
    <canvas ref="canvasEl" class="garden-canvas" />

    <!-- Seletor de tema — canto superior direito -->
    <div class="theme-bar">
      <button
        v-for="t in THEMES"
        :key="t.id"
        class="theme-btn"
        :class="{ 'theme-btn-active': isCurrent(t.id) }"
        :title="t.label"
        @click="setTheme(t.id)"
      >
        <UIcon :name="t.icon" class="w-3.5 h-3.5" />
      </button>
    </div>

    <!-- Conteúdo -->
    <div class="garden-content" :class="centered ? 'garden-centered' : 'garden-scroll'">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { THEMES } from '~/composables/useTheme'

const { currentTheme, setTheme, isCurrent, initTheme, isDark } = useTheme()

// Overlay mais leve em temas claros → imagem aparece mais; mais denso em temas escuros
const effectiveOverlay = computed(() =>
  isDark.value ? props.overlayOpacity : props.overlayOpacity * 0.72
)

const props = withDefaults(defineProps<{
  imgOpacity?: number
  overlayOpacity?: number
  /** false = conteúdo ocupa posição natural (para páginas de scroll); true = centralizado (login) */
  centered?: boolean
}>(), {
  imgOpacity: 0.28,
  overlayOpacity: 0.62,
  centered: true,
})

const canvasEl = ref<HTMLCanvasElement | null>(null)
let rafId = 0

// ── Paths: curvas bezier cúbicas (coordenadas normalizadas 0-1) ──────────────
// Cada path: [p0, p1, p2, p3] pontos de controle [x, y]
const PATHS: [number, number][][] = [
  // Galhos principais → flores superiores
  [[0.50,0.65],[0.50,0.45],[0.50,0.25],[0.50,0.03]],   // → flor topo centro
  [[0.50,0.65],[0.47,0.44],[0.44,0.24],[0.42,0.05]],   // → flor topo centro-esq
  [[0.50,0.65],[0.53,0.44],[0.57,0.24],[0.60,0.06]],   // → flor topo centro-dir
  [[0.50,0.65],[0.45,0.48],[0.38,0.30],[0.31,0.11]],   // → flor topo esquerda
  [[0.50,0.65],[0.56,0.48],[0.64,0.30],[0.71,0.13]],   // → flor topo direita
  // Galhos laterais → flores das bordas
  [[0.50,0.62],[0.38,0.58],[0.27,0.52],[0.18,0.40]],   // → flor esquerda
  [[0.50,0.62],[0.62,0.62],[0.71,0.56],[0.79,0.52]],   // → flor direita
  // Galho → florzinha inferior esquerda
  [[0.50,0.68],[0.42,0.70],[0.34,0.68],[0.27,0.63]],
  // Raízes descendo
  [[0.50,0.72],[0.44,0.80],[0.37,0.87],[0.28,0.96]],   // raiz esq
  [[0.50,0.72],[0.56,0.80],[0.63,0.87],[0.72,0.96]],   // raiz dir
  [[0.50,0.72],[0.49,0.83],[0.47,0.91],[0.45,1.00]],   // raiz centro
]

// Posições das flores para os blooms (x, y normalizado)
const FLOWERS: [number, number][] = [
  [0.50, 0.03], [0.42, 0.05], [0.60, 0.06],
  [0.31, 0.11], [0.71, 0.13],
  [0.18, 0.40], [0.79, 0.52],
  [0.27, 0.63],
]

const PALETTE = [
  'rgba(167,139,250,',
  'rgba(129,140,248,',
  'rgba(196,181,253,',
  'rgba(255,255,255,',
  'rgba(6,182,212,',
  'rgba(232,196,255,',
]

// ── Bezier helpers ────────────────────────────────────────────────────────────
function bezierPt(path: [number,number][], t: number): [number, number] {
  const [p0, p1, p2, p3] = path
  const mt = 1 - t
  return [
    mt*mt*mt*p0[0] + 3*mt*mt*t*p1[0] + 3*mt*t*t*p2[0] + t*t*t*p3[0],
    mt*mt*mt*p0[1] + 3*mt*mt*t*p1[1] + 3*mt*t*t*p2[1] + t*t*t*p3[1],
  ]
}

// ── Particle ──────────────────────────────────────────────────────────────────
interface Particle {
  pathIdx: number
  t: number
  speed: number
  color: string
  radius: number
  glow: number
  trail: [number, number][]
  trailLen: number
}

function mkParticle(): Particle {
  return {
    pathIdx:  Math.floor(Math.random() * PATHS.length),
    t:        Math.random(),
    speed:    0.0008 + Math.random() * 0.0018,
    color:    PALETTE[Math.floor(Math.random() * PALETTE.length)],
    radius:   1.4 + Math.random() * 1.8,
    glow:     8 + Math.random() * 14,
    trail:    [],
    trailLen: 6 + Math.floor(Math.random() * 8),
  }
}

// ── Bloom ─────────────────────────────────────────────────────────────────────
interface Bloom {
  fx: number; fy: number
  t: number; speed: number
  maxR: number; rgb: string
  active: boolean
}

function mkBloom(fx: number, fy: number): Bloom {
  return {
    fx, fy, t: 0, active: true,
    speed: 0.0022 + Math.random() * 0.001,  // ~6–8s vida
    maxR: 28 + Math.random() * 36,
    rgb: Math.random() > 0.45 ? '192,181,253' : Math.random() > 0.5 ? '255,255,255' : '129,140,248',
  }
}

// ── Draw helpers ──────────────────────────────────────────────────────────────
function drawParticle(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  colorBase: string, alpha: number,
  radius: number, glow: number
) {
  ctx.save()
  ctx.globalAlpha = alpha
  ctx.shadowBlur  = glow
  ctx.shadowColor = colorBase + '1)'
  ctx.fillStyle   = colorBase + alpha + ')'
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function drawBloom(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  t: number, maxR: number, rgb: string
) {
  // Expansão rápida no início, desvanece ao final
  const r = maxR * Math.pow(t, 0.55)
  const alpha = t < 0.25 ? (t / 0.25) * 0.45 : ((1 - t) / 0.75) * 0.45

  // Círculo de brilho externo
  const outer = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
  outer.addColorStop(0,   `rgba(255,255,255,${alpha * 1.4})`)
  outer.addColorStop(0.25,`rgba(${rgb},${alpha})`)
  outer.addColorStop(0.7, `rgba(${rgb},${alpha * 0.3})`)
  outer.addColorStop(1,   `rgba(${rgb},0)`)
  ctx.save()
  ctx.fillStyle = outer
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()

  // Pétala central pequena e brilhante
  if (r > 4) {
    const inner = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.25)
    inner.addColorStop(0, `rgba(255,255,255,${alpha * 2})`)
    inner.addColorStop(1, `rgba(${rgb},0)`)
    ctx.fillStyle = inner
    ctx.beginPath()
    ctx.arc(cx, cy, r * 0.25, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

// ── Animation loop ────────────────────────────────────────────────────────────
function startAnimation() {
  const canvas = canvasEl.value!
  if (!canvas) return
  const ctx = canvas.getContext('2d')!

  const particles: Particle[] = Array.from({ length: 28 }, mkParticle)
  const blooms: Bloom[] = []

  // Stagger inicial dos blooms para não explodir tudo de uma vez
  FLOWERS.forEach((f, i) => {
    setTimeout(() => blooms.push(mkBloom(f[0], f[1])), i * 900)
  })

  let lastBloom = 0

  function resize() {
    canvas!.width  = canvas!.offsetWidth  * devicePixelRatio
    canvas!.height = canvas!.offsetHeight * devicePixelRatio
    ctx.scale(devicePixelRatio, devicePixelRatio)
  }
  resize()
  window.addEventListener('resize', resize)

  function frame() {
    const W = canvas!.offsetWidth
    const H = canvas!.offsetHeight
    ctx.clearRect(0, 0, canvas!.width, canvas!.height)

    // ── Partículas ──────────────────────────────────────────────────────────
    for (const p of particles) {
      p.t += p.speed
      if (p.t > 1) { p.t = 0; p.trail = [] }

      const [nx, ny] = bezierPt(PATHS[p.pathIdx], p.t)
      const x = nx * W, y = ny * H

      // Trail
      for (let i = 0; i < p.trail.length; i++) {
        const trailAlpha = (i / p.trail.length) * 0.25
        const [tx, ty] = p.trail[i]
        drawParticle(ctx, tx, ty, p.color, trailAlpha, p.radius * 0.55, p.glow * 0.3)
      }
      p.trail.push([x, y])
      if (p.trail.length > p.trailLen) p.trail.shift()

      // Cabeça da partícula
      const headAlpha = 0.7 + Math.sin(Date.now() / 400 + p.pathIdx) * 0.15
      drawParticle(ctx, x, y, p.color, headAlpha, p.radius, p.glow)
    }

    // ── Blooms ──────────────────────────────────────────────────────────────
    const now = Date.now()
    for (let i = blooms.length - 1; i >= 0; i--) {
      const b = blooms[i]
      b.t += b.speed
      if (b.t >= 1) { b.active = false }
      if (!b.active) {
        // Reagenda bloom com delay aleatório (4–12s)
        const f = FLOWERS[Math.floor(Math.random() * FLOWERS.length)]
        const delay = 4000 + Math.random() * 8000
        setTimeout(() => blooms.splice(i, 0, mkBloom(f[0], f[1])), delay)
        blooms.splice(i, 1)
        continue
      }
      drawBloom(ctx, b.fx * W, b.fy * H, b.t, b.maxR, b.rgb)
    }

    // Spawn bloom extra esporádico (garante densidade mínima)
    if (now - lastBloom > 3500 && blooms.length < FLOWERS.length) {
      const f = FLOWERS[Math.floor(Math.random() * FLOWERS.length)]
      blooms.push(mkBloom(f[0], f[1]))
      lastBloom = now
    }

    // ── Shimmer radial sutil no centro do cérebro ────────────────────────────
    const cx = W * 0.50, cy = H * 0.52
    const shimmerAlpha = 0.018 + Math.sin(Date.now() / 2800) * 0.008
    const shimmer = ctx.createRadialGradient(cx, cy, 0, cx, cy, W * 0.25)
    shimmer.addColorStop(0,   `rgba(192,181,253,${shimmerAlpha * 2})`)
    shimmer.addColorStop(0.5, `rgba(129,140,248,${shimmerAlpha})`)
    shimmer.addColorStop(1,   `rgba(129,140,248,0)`)
    ctx.save()
    ctx.fillStyle = shimmer
    ctx.beginPath()
    ctx.arc(cx, cy, W * 0.25, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    rafId = requestAnimationFrame(frame)
  }

  rafId = requestAnimationFrame(frame)
  return () => {
    cancelAnimationFrame(rafId)
    window.removeEventListener('resize', resize)
  }
}

onMounted(async () => {
  await initTheme()
  const cleanup = startAnimation()
  onUnmounted(() => cleanup?.())
})
</script>

<style scoped>
.garden-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: var(--bg-page, #07071a);
  display: flex;
  flex-direction: column;
}
.garden-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center 30%;
  background-repeat: no-repeat;
  pointer-events: none;
}
.garden-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 55%, #1a1040 0%, #0d0a2a 45%, #07071a 100%);
  pointer-events: none;
  transition: opacity 0.4s ease;
}
.garden-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
/* ── Seletor de tema ─────────────────────────────────────────────────────────── */
.theme-bar {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 20;
  display: flex;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: 2rem;
  background: rgba(10, 8, 30, 0.55);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(129, 140, 248, 0.18);
}
.theme-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  color: rgba(196, 181, 253, 0.6);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.theme-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  color: #fff;
}
.theme-btn-active {
  background: rgba(99, 102, 241, 0.35);
  border-color: rgba(129, 140, 248, 0.5);
  color: #fff;
}

.garden-content {
  position: relative;
  z-index: 10;
  flex: 1;
  min-height: 0;
}
.garden-centered {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}
.garden-scroll {
  display: block;
  overflow-y: auto;
  height: 100%;
}
</style>
